import { FC, useState, useCallback, useMemo, memo } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { Pause, Play, CircleGauge, MessageCircle } from 'lucide-react';

import { useScreenSizes } from '@hooks/useScreenSizes';
import { theme } from 'theme';
import { RECORDED_SHOW_PLAYER_HEIGHT } from '@utils/constants';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import MobileChat from '@components/show/mobileChat';
import { DefaultText } from '@components/text/Text';

const Container = styled.div`
  max-height: ${RECORDED_SHOW_PLAYER_HEIGHT}px;
  width: 100%;
  background: ${({ theme }) => theme.colors.black};
  display: flex;
  flex-direction: column;
  padding: 8px 16px 16px;
  overflow: visible;
`;

const SliderContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
  gap: 4px;
`;

const TimeLabels = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TimeText = styled(DefaultText)`
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
  font-weight: ${({ theme }) => theme.fonts.fontWeight.medium};
`;

const SliderWrapper = styled.div`
  width: 100%;
  padding: 8px 0;

  .rc-slider {
    position: relative;
    height: 6px;
    padding: 0;
    width: 100%;
    border-radius: 3px;
    touch-action: none;
    box-sizing: border-box;
    -webkit-tap-highlight-color: ${({ theme }) => theme.colors.transparent};
  }

  .rc-slider-rail {
    position: absolute;
    width: 100%;
    height: 6px;
    background-color: ${({ theme }) => theme.colors.grey[700]};
    border-radius: 3px;
  }

  .rc-slider-track {
    position: absolute;
    height: 6px;
    background-color: ${({ theme }) => theme.colors.white};
    border-radius: 3px;
  }

  .rc-slider-handle {
    position: absolute;
    width: 16px;
    height: 16px;
    margin-top: -5px;
    background-color: ${({ theme }) => theme.colors.white};
    border: none;
    border-radius: 50%;
    cursor: grab;
    box-shadow: 0 2px 8px
      ${({ theme }) => `${theme.colors.black}${theme.opacity[30]}`};
    touch-action: pan-x;

    &:active {
      cursor: grabbing;
      box-shadow: 0 0 0 5px
        ${({ theme }) => `${theme.colors.white}${theme.opacity[20]}`};
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 5px
        ${({ theme }) => `${theme.colors.white}${theme.opacity[20]}`};
    }

    &:hover {
      box-shadow: 0 0 0 5px
        ${({ theme }) => `${theme.colors.white}${theme.opacity[10]}`};
    }
  }

  .rc-slider-handle-dragging {
    box-shadow: 0 0 0 5px
      ${({ theme }) => `${theme.colors.white}${theme.opacity[20]}`};
  }
`;

const ControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 8px;
  overflow: visible;
`;

const IconButton = styled(TouchableOpacity)`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const PlayPauseButton = styled(IconButton)`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 3px solid ${({ theme }) => theme.colors.white};
  padding: 0;
`;

const SpeedButton = styled(IconButton)``;

const SpeedContainer = styled.div`
  position: relative;
  overflow: visible;
`;

const SpeedMenu = styled.div<{ $isOpen: boolean }>`
  min-width: 80px;
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 8px;
  background: ${({ theme }) => theme.colors.grey[800]};
  border-radius: 8px;
  padding: 8px;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  flex-direction: column;
  gap: 4px;
  box-shadow: 0 4px 12px
    ${({ theme }) => `${theme.colors.black}${theme.opacity[50]}`};
  z-index: 10;
`;

const SpeedOption = styled.button<{ $isActive: boolean }>`
  background: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.grey[700] : theme.colors.transparent};
  border: none;
  color: ${({ theme }) => theme.colors.white};
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
  font-family: ${({ theme }) => theme.fonts.fontFamily.primary};
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.grey[700]};
  }
`;

type Props = {
  showId: string;
  isPaused: boolean;
  duration: number;
  sliderValue: number;
  rate: number;
  isDisabled: boolean;
  onPlay: () => void;
  onPause: () => void;
  onSeek: (value: number | number[]) => void;
  onSpeedChange: (speed: number) => void;
};

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const SPEED_OPTIONS = [1, 1.5, 2];

const CustomAudioPlayer: FC<Props> = ({
  showId,
  isPaused,
  duration,
  sliderValue,
  rate,
  isDisabled,
  onPlay,
  onPause,
  onSeek,
  onSpeedChange,
}) => {
  const [isSpeedMenuOpen, setIsSpeedMenuOpen] = useState<boolean>(false);
  const [isMobileChatOpen, setIsMobileChatOpen] = useState<boolean>(false);

  const currentTime = useMemo(
    () => formatTime((sliderValue / 100) * duration),
    [sliderValue, duration],
  );
  const totalTime = useMemo(() => formatTime(duration), [duration]);
  const { isMobile, isTablet } = useScreenSizes();

  const handlePlayPause = useCallback(() => {
    if (isPaused) {
      onPlay();
    } else {
      onPause();
    }
  }, [isPaused, onPause, onPlay]);

  const handleSpeedSelect = useCallback(
    (speed: number) => {
      onSpeedChange(speed);
      setIsSpeedMenuOpen(false);
    },
    [onSpeedChange],
  );

  const toggleSpeedMenu = useCallback(() => {
    setIsSpeedMenuOpen((prev) => !prev);
  }, []);

  const toggleMobileChat = useCallback(() => {
    setIsMobileChatOpen((prev) => !prev);
  }, []);

  return (
    <Container>
      <SliderContainer>
        <TimeLabels>
          <TimeText>{currentTime}</TimeText>

          <TimeText>{totalTime}</TimeText>
        </TimeLabels>

        <SliderWrapper>
          <Slider
            min={0}
            max={100}
            value={sliderValue}
            onChange={onSeek}
            disabled={isDisabled}
          />
        </SliderWrapper>
      </SliderContainer>

      <ControlsContainer>
        {isMobile || isTablet ? (
          <IconButton onClick={toggleMobileChat}>
            <MessageCircle
              size={24}
              strokeWidth={2}
              color={theme.colors.white}
            />
          </IconButton>
        ) : (
          <div />
        )}

        <PlayPauseButton onClick={handlePlayPause} disabled={isDisabled}>
          {isPaused ? (
            <Play
              size={24}
              strokeWidth={3}
              color={theme.colors.white}
              fill={theme.colors.white}
            />
          ) : (
            <Pause
              size={24}
              strokeWidth={3}
              color={theme.colors.white}
              fill={theme.colors.white}
            />
          )}
        </PlayPauseButton>

        <SpeedContainer>
          <SpeedButton onClick={toggleSpeedMenu} disabled={isDisabled}>
            <CircleGauge size={24} strokeWidth={2} color={theme.colors.white} />
          </SpeedButton>

          <SpeedMenu $isOpen={isSpeedMenuOpen}>
            {SPEED_OPTIONS.map((speed) => (
              <SpeedOption
                key={speed}
                $isActive={rate === speed}
                onClick={() => handleSpeedSelect(speed)}
              >
                {speed}x
              </SpeedOption>
            ))}
          </SpeedMenu>
        </SpeedContainer>
      </ControlsContainer>

      {isMobile || isTablet
        ? createPortal(
            <MobileChat
              showId={showId}
              isOpen={isMobileChatOpen}
              onClose={toggleMobileChat}
            />,
            document.body,
          )
        : null}
    </Container>
  );
};

export default memo(CustomAudioPlayer);
